import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';

import localUtil from '../../lib/util';

@withText(props => ({
    searchPlaceholder: <Text id='admin.search-users-placeholder'>Search by name, email, or phone...</Text>,
    selectUser: <Text id='admin.select-user'>Select User</Text>,
    noResults: <Text id='admin.no-results'>No users found</Text>,
    cancel: <Text id='admin.cancel'>Cancel</Text>,
    currentUser: <Text id='admin.current-user'>Current User</Text>,
}))
@observer
class UserPicker extends Component {
    constructor(props) {
        super(props);
        this.state = {
            searchText: '',
            searching: false,
        };
        this.searchTimer = null;
    }

    searchInput = (e) => {
        const search = e.target.value;
        this.setState({ searchText: search });

        clearTimeout(this.searchTimer);
        if (search.length >= 2) {
            this.searchTimer = setTimeout(async () => {
                this.doSearch();
            }, 500);
        }
    }

    doSearch = async () => {
        const { userStore } = this.props.stores;
        const { searchText } = this.state;
        if (searchText.length >= 2) {
            this.setState({ searching: true });
            await userStore.searchUsersAll({ search: searchText });
            this.setState({ searching: false });
        }
    }

    handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.doSearch();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.onClickCancel();
        }
    }

    onClickSelectUser = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('.user-item').dataset;
        const { callback, onSelect } = this.props;

        // Call the onSelect callback with the selected user ID
        if (onSelect) {
            await onSelect(parseInt(id, 10));
        }
        if (callback) {
            callback();
        }

        // Close drawer
        this.onClickCancel();
    }

    onClickCancel = () => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        appState.toggleDrawer(false, drawerLevel);
    }

    componentDidMount() {
        const { userStore } = this.props.stores;
        // Clear previous search results
        userStore.localUpdateField('searchUsersResult', []);
    }

    render() {
        const { searchText, searching } = this.state;
        const {
            searchPlaceholder,
            selectUser,
            noResults,
            cancel,
            currentUser,
        } = this.props;
        const { userStore } = this.props.stores;
        const { user, searchUsersResult } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');
        const { currentUserId, currentUserName } = this.props;

        return (
            <div class='w-100 d-flex flex-column' style='min-height: 100%;'>
                <div class='font-weight-lighter px-3 py-3 box-header d-flex flex-row justify-content-between'>
                    <h5>
                        <i class='fa-solid fa-user-pen me-2'></i>
                        {selectUser}
                    </h5>
                </div>

                {currentUserId && (
                    <div class={`mx-3 mb-3 px-3 py-2 rounded ${darkmode ? 'bg-secondary' : 'bg-light'}`}>
                        <small class='text-muted'>{currentUser}:</small>
                        <div class='font-weight-bold'>
                            ID: {currentUserId} - {currentUserName || 'Unknown'}
                        </div>
                    </div>
                )}

                <div class={`d-flex flex-column mx-3 px-3 py-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>
                    <div class='input-group input-group-sm'>
                        <div class='input-group-text rounded-pill-left'>
                            <i class={`fa-regular fa-magnifying-glass ${searching ? 'fa-spin' : ''}`}></i>
                        </div>
                        <input
                            class='form-control rounded-pill-right'
                            type='text'
                            value={searchText}
                            placeholder={searchPlaceholder}
                            onInput={this.searchInput}
                            onKeyDown={this.handleKeyDown}
                            autoFocus
                        />
                    </div>
                </div>

                <div class='flex-fill overflow-auto mx-3 mt-2' style='max-height: calc(100vh - 300px);'>
                    {searchUsersResult && searchUsersResult.length > 0 ? (
                        <div class='d-flex flex-column'>
                            {searchUsersResult.map((u, idx) => {
                                const image = localUtil.getImage({ user: u });
                                const isCurrentUser = u.id === currentUserId;
                                return (
                                    <div
                                        key={u.id}
                                        class={`user-item d-flex flex-row align-items-center py-2 px-3 mb-1 rounded ${darkmode ? 'bg-darkmode' : 'bg-lightmode'} ${isCurrentUser ? 'border border-primary' : ''}`}
                                        style='cursor: pointer;'
                                        data-id={u.id}
                                        onClick={this.onClickSelectUser}
                                    >
                                        <div
                                            class={`${darkmode ? 'bg-secondary' : 'bg-light'} rounded-circle d-flex justify-content-center align-items-center me-3`}
                                            style={`width: 40px; height: 40px; ${image ? `background-image: url('${image}'); background-size: cover;` : ''}`}
                                        >
                                            {!image && (
                                                <span class='font-weight-lighter'>
                                                    {u.firstname ? u.firstname[0] : '?'}
                                                </span>
                                            )}
                                        </div>
                                        <div class='flex-fill'>
                                            <div class='font-weight-bold'>
                                                {u.firstname} {u.lastname}
                                                {isCurrentUser && (
                                                    <span class='badge bg-primary ms-2'>
                                                        <Text id='admin.current'>Current</Text>
                                                    </span>
                                                )}
                                            </div>
                                            <small class='text-muted'>
                                                ID: {u.id}
                                                {u.email && <span class='ms-2'>{u.email}</span>}
                                                {u.cellphone && <span class='ms-2'>{u.cellphone}</span>}
                                            </small>
                                        </div>
                                        <div>
                                            <i class='fa-solid fa-chevron-right text-muted'></i>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : searchText.length >= 2 && !searching ? (
                        <div class='text-center text-muted py-4'>
                            <i class='fa-solid fa-user-slash fa-2x mb-2'></i>
                            <div>{noResults}</div>
                        </div>
                    ) : searchText.length > 0 && searchText.length < 2 ? (
                        <div class='text-center text-muted py-4'>
                            <small>
                                <Text id='admin.type-more'>Type at least 2 characters to search</Text>
                            </small>
                        </div>
                    ) : null}
                </div>

                <div class='d-flex flex-row justify-content-end mx-3 my-3'>
                    <button
                        type='button'
                        class='btn btn-outline-secondary rounded-pill'
                        onClick={this.onClickCancel}
                    >
                        <i class='fa-solid fa-times me-2'></i>
                        {cancel}
                    </button>
                </div>
            </div>
        );
    }
}

export default UserPicker;
